"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APP_BASE_SOURCE_QUERY_DELTA_TOKEN_HOLD_BYTES = exports.APP_BASE_SOURCE_QUERY_DELTA_HOLD_BYTES = exports.APP_BASE_SOURCE_QUERY_DELTA_HOLD_MS = exports.APP_BASE_SOURCE_QUERY_TRANSPORT_HOLD_BYTES = exports.APP_BASE_SOURCE_QUERY_TRANSPORT_HOLD_MS = exports.APP_BASE_SOURCE_QUERY_TRANSPORT_PROTOCOL = exports.AppBaseSourceQueryError = exports.APP_BASE_SOURCE_UNAVAILABLE_REASONS = exports.APP_BASE_SOURCE_QUERY_MAX_GROUPS = exports.APP_BASE_AUTHORING_QUERY_MAX_ROWS = exports.APP_BASE_SOURCE_QUERY_TRANSPORT_FIELDS = exports.APP_BASE_SOURCE_QUERY_STATE_SHARD = void 0;
exports.isAppBaseSourceUnavailableReason = isAppBaseSourceUnavailableReason;
exports.appBaseSourceUnavailableError = appBaseSourceUnavailableError;
exports.parsePortableAppBaseSourceQuery = parsePortableAppBaseSourceQuery;
exports.appBaseQueryIdentity = appBaseQueryIdentity;
exports.withAppBaseOwnedRows = withAppBaseOwnedRows;
exports.withAppBaseLinkedRows = withAppBaseLinkedRows;
exports.resolveAppBaseLinkedFrom = resolveAppBaseLinkedFrom;
exports.executePortableAppBaseSourceQuery = executePortableAppBaseSourceQuery;
exports.executePortableAppBaseSourceQueryWindows = executePortableAppBaseSourceQueryWindows;
exports.createAppBaseSourceQueryTransportPage = createAppBaseSourceQueryTransportPage;
exports.createAppBaseHeldValues = createAppBaseHeldValues;
exports.createAppBaseSourceQueryDeltas = createAppBaseSourceQueryDeltas;
exports.createAppBaseSourceQueryTransportHold = createAppBaseSourceQueryTransportHold;
const database_identity_1 = require("@streamlain/database-identity");
const app_base_bridge_budgets_1 = require("./app-base-bridge-budgets");
Object.defineProperty(exports, "APP_BASE_SOURCE_QUERY_TRANSPORT_FIELDS", { enumerable: true, get: function () { return app_base_bridge_budgets_1.APP_BASE_SOURCE_QUERY_TRANSPORT_FIELDS; } });
const app_base_contract_1 = require("./app-base-contract");
const app_base_source_query_schema_1 = require("./app-base-source-query-schema");
/** The shard name of the one entry of `revisionVector` in a result. */
exports.APP_BASE_SOURCE_QUERY_STATE_SHARD = 'state';
exports.APP_BASE_AUTHORING_QUERY_MAX_ROWS = 500;
exports.APP_BASE_SOURCE_QUERY_MAX_GROUPS = 500;
/** Text columns group by their exact stored plain text. */
const APP_BASE_TEXT_GROUP_TYPES = new Set(['rich-text', 'text', 'title']);
const APP_BASE_GROUP_TYPES = new Set([
    'multi-select',
    'relation',
    'select',
    ...APP_BASE_TEXT_GROUP_TYPES,
]);
/**
 * Why a source cannot be delivered although the request is valid. Apps and
 * App Studio see only this reason, never byte budgets or transport sizes.
 */
exports.APP_BASE_SOURCE_UNAVAILABLE_REASONS = [
    'stored_value_too_large',
    'sort_values_too_large',
    'data_source_trashed',
    'data_source_deleted',
    // An install may not bind the table; its manager has an attach request.
    'data_source_pending',
];
/** True for stored-data failures (HTTP 422); other reasons are outages. */
function isAppBaseSourceUnavailableReason(reason) {
    return exports.APP_BASE_SOURCE_UNAVAILABLE_REASONS.includes(reason);
}
class AppBaseSourceQueryError extends Error {
    constructor(code, message, details = {}) {
        super(message);
        this.code = code;
        this.details = details;
        this.name = 'AppBaseSourceQueryError';
    }
}
exports.AppBaseSourceQueryError = AppBaseSourceQueryError;
const UNAVAILABLE_MESSAGES = {
    stored_value_too_large: 'Data source contains a stored value above the platform limit',
    sort_values_too_large: 'Data source sort values are too large for this query',
    data_source_trashed: 'Data source is in the trash',
    data_source_deleted: 'Data source was deleted',
    data_source_pending: 'Data source waits for approval by a manager',
};
function appBaseSourceUnavailableError(sourceKey, reason) {
    return new AppBaseSourceQueryError('APP_BASE_SOURCE_UNAVAILABLE', UNAVAILABLE_MESSAGES[reason], { sourceKey, reason });
}
function parsePortableAppBaseSourceQuery(input, columns, maxWindowRows = app_base_contract_1.APP_BASE_BUDGETS.maxDatasetRows) {
    const pending = [{ value: input, depth: 0 }];
    let nodes = 0;
    while (pending.length) {
        const { value, depth } = pending.pop();
        if (++nodes > 2048 || depth > 16) {
            throw new AppBaseSourceQueryError('APP_BASE_QUERY_INVALID', 'App query is too complex');
        }
        if (value && typeof value === 'object') {
            for (const child of Object.values(value)) {
                pending.push({ value: child, depth: depth + 1 });
            }
        }
    }
    const parsed = app_base_source_query_schema_1.AppBaseSourceQuerySchema.safeParse(input);
    if (!parsed.success) {
        throw new AppBaseSourceQueryError('APP_BASE_QUERY_INVALID', 'Invalid App source query', { path: parsed.error.issues[0]?.path.join('.') ?? '' });
    }
    const query = parsed.data;
    if (query.limit > maxWindowRows) {
        throw new AppBaseSourceQueryError('APP_BASE_DATA_BUDGET_EXCEEDED', 'App query window exceeds its row budget', {
            budget: 'row_count',
            limit: maxWindowRows,
            requested: query.limit,
        });
    }
    const allowed = new Set(columns.map(column => column.id));
    const requireColumn = (id) => {
        if (!allowed.has(id)) {
            throw new AppBaseSourceQueryError('APP_BASE_QUERY_FORBIDDEN_COLUMN', 'App query references a column that is not exposed');
        }
    };
    const visitPredicate = (node) => {
        if (node.type === 'predicate-group') {
            node.predicates.forEach(visitPredicate);
        }
        else if (node.type === 'text-search') {
            node.propertyIds?.forEach(requireColumn);
        }
        else if (node.type === 'column') {
            requireColumn(node.propertyId);
        }
        else {
            requireColumn(node.startPropertyId);
            if (node.endPropertyId)
                requireColumn(node.endPropertyId);
        }
    };
    const visitFilter = (node) => {
        if (node.type === 'group') {
            node.conditions.forEach(visitFilter);
        }
        else {
            requireColumn(node.left.name);
            if (!(0, database_identity_1.getDatabaseViewFilterFunction)(node.function, columns.find(column => column.id === node.left.name)?.type)) {
                throw new AppBaseSourceQueryError('APP_BASE_QUERY_INVALID', 'Unsupported App filter operator');
            }
        }
    };
    query.columnIds?.forEach(id => {
        if (!allowed.has(id)) {
            throw new AppBaseSourceQueryError('APP_BASE_QUERY_INVALID', 'App query selects a column that is not exposed', { columnId: id });
        }
    });
    query.searchColumnIds?.forEach(requireColumn);
    query.predicates.forEach(visitPredicate);
    if (query.filter)
        visitFilter(query.filter);
    query.sort.forEach(entry => requireColumn(entry.propertyId));
    query.groupBy.forEach(id => {
        requireColumn(id);
        const column = columns.find(column => column.id === id);
        const type = column?.type === 'rollup' ? column.grouping?.type : column?.type;
        if (!type || !APP_BASE_GROUP_TYPES.has(type)) {
            throw new AppBaseSourceQueryError('APP_BASE_QUERY_INVALID', 'App queries may group only by relation, select, text, title, or compatible rollup columns');
        }
    });
    query.aggregates.forEach(entry => {
        if (entry.type !== 'count' && !entry.propertyId) {
            throw new AppBaseSourceQueryError('APP_BASE_QUERY_INVALID', 'Aggregate column is required');
        }
        if (entry.propertyId)
            requireColumn(entry.propertyId);
    });
    return query;
}
function appBaseQueryIdentity(value) {
    if (Array.isArray(value)) {
        return '[' + value.map(appBaseQueryIdentity).join(',') + ']';
    }
    if (value && typeof value === 'object') {
        return ('{' +
            Object.entries(value)
                .filter(([, child]) => child !== undefined)
                .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
                .map(([key, child]) => JSON.stringify(key) + ':' + appBaseQueryIdentity(child))
                .join(',') +
            '}');
    }
    return JSON.stringify(value) ?? 'null';
}
const encoder = new TextEncoder();
const byteLength = (value) => encoder.encode(JSON.stringify(value)).byteLength;
/**
 * Replaces the `owner: 'me'` filter by the row IDs the server resolved for the
 * requesting principal, intersected with any requested row IDs. Server readers
 * and local-first hosts both run it before taking the query identity, so
 * cached windows key on the resolved IDs.
 */
function withAppBaseOwnedRows(query, owned) {
    const requested = query.rowIds ? new Set(query.rowIds) : null;
    return {
        ...query,
        owner: undefined,
        rowIds: owned.filter(id => !requested || requested.has(id)),
    };
}
/**
 * Replaces `linkedFrom` by the row IDs the origin rows link, intersected with
 * any requested row IDs, in the order the origin links them. Every host runs
 * it (through resolveAppBaseLinkedFrom) before taking the query identity.
 */
function withAppBaseLinkedRows(query, linked) {
    const requested = query.rowIds ? new Set(query.rowIds) : null;
    return {
        ...query,
        linkedFrom: undefined,
        rowIds: linked.filter(id => !requested || requested.has(id)),
    };
}
/**
 * Resolves `linkedFrom` of a query: `readOrigin` reads the relation cells of
 * the origin rows as the host reads that source for the same viewer on the
 * same route (refusing it as a direct query would), and
 * withAppBaseLinkedRows takes the IDs they link. The origin rows are those
 * named by ID, those matching a condition, or those an inner `linkedFrom`
 * names, resolved first the same way. A query without it is returned as it
 * is.
 */
async function resolveAppBaseLinkedFrom(query, readOrigin) {
    const { linkedFrom } = query;
    if (!linkedFrom)
        return query;
    // The origin rows as a query of the origin: an inner step resolves to the
    // IDs it names.
    const origin = 'linkedFrom' in linkedFrom
        ? {
            rowIds: (await resolveAppBaseLinkedFrom(app_base_source_query_schema_1.AppBaseSourceQuerySchema.parse({
                linkedFrom: linkedFrom.linkedFrom,
            }), readOrigin)).rowIds,
        }
        : 'rowIds' in linkedFrom
            ? { rowIds: linkedFrom.rowIds }
            : { predicates: linkedFrom.predicates, filter: linkedFrom.filter };
    const linked = new Set();
    // The origin rows are named in parts of the rows one query reads. Each
    // window names no limit: the host answers as many rows as it answers a
    // query without one (a binding's row budget may be below one query's rows),
    // and the next window starts after them, until the origin answered every
    // row it holds.
    const step = app_base_contract_1.APP_BASE_BUDGETS.maxDatasetRows;
    const parts = [];
    if (origin.rowIds)
        for (let start = 0; start < origin.rowIds.length; start += step)
            parts.push(origin.rowIds.slice(start, start + step));
    else
        parts.push(undefined);
    for (const rowIds of parts) {
        for (let offset = 0;;) {
            const read = await readOrigin(linkedFrom.sourceKey, {
                ...(rowIds
                    ? { rowIds, rowColumns: 'selected' }
                    : { predicates: origin.predicates, filter: origin.filter }),
                columnIds: [linkedFrom.columnId],
                offset,
            });
            if (read.columns.find(column => column.id === linkedFrom.columnId)?.type !==
                'relation')
                throw new AppBaseSourceQueryError('APP_BASE_QUERY_INVALID', 'linkedFrom names no relation column of its origin', { columnId: linkedFrom.columnId });
            for (const row of read.rows) {
                const value = row.cells[linkedFrom.columnId]?.value;
                if (Array.isArray(value))
                    for (const id of value)
                        if (typeof id === 'string')
                            linked.add(id);
            }
            offset += read.rows.length;
            if (offset >= read.totalCount)
                break;
            // A window that brings no row while rows remain holds a row larger
            // than one answer.
            if (!read.rows.length)
                throw appBaseSourceUnavailableError(linkedFrom.sourceKey, 'stored_value_too_large');
        }
    }
    return withAppBaseLinkedRows(query, [...linked]);
}
/** Shards an exact-row read loads at the same time. */
const EXACT_ROW_READ_CONCURRENCY = 8;
/** The bytes of rows one answer brings, its further windows included. */
const APP_BASE_QUERY_WINDOW_BYTES = 8 * 1024 * 1024;
async function executePortableAppBaseSourceQuery(input) {
    const { source, query, revision, revisionVector, signal, maxWorkingBytes, cache, } = input;
    const maxWindowBytes = input.maxWindowBytes ?? APP_BASE_QUERY_WINDOW_BYTES;
    if (!Number.isSafeInteger(maxWindowBytes) || maxWindowBytes < 1)
        throw new AppBaseSourceQueryError('APP_BASE_QUERY_INVALID', 'Invalid internal query window size');
    if (query.owner)
        throw new AppBaseSourceQueryError('APP_BASE_QUERY_INVALID', 'Row owner filters require server-side ownership resolution');
    if (query.linkedFrom)
        throw new AppBaseSourceQueryError('APP_BASE_QUERY_INVALID', 'Linked rows require resolution by the host before execution');
    const yieldControl = input.yieldControl ?? (() => Promise.resolve());
    const { index } = source;
    const requestedIds = query.rowIds ? new Set(query.rowIds) : null;
    // The columns each returned row carries: exact-row reads are details with
    // every column, unless they ask for the selected ones.
    const projectedColumnIds = query.columnIds !== undefined &&
        (requestedIds === null || query.rowColumns === 'selected')
        ? query.columnIds
        : null;
    const selectedShards = requestedIds === null
        ? null
        : new Set(await source.selectRowShards([...requestedIds]));
    if (selectedShards &&
        [...selectedShards].some(id => !index.shards.some(shard => shard.shardId === id))) {
        throw new AppBaseSourceQueryError('APP_BASE_QUERY_REVISION_CHANGED', 'Exact row lookup left the admitted shard set');
    }
    const predicates = [...query.predicates];
    if (query.search?.trim()) {
        predicates.push({
            type: 'text-search',
            query: query.search,
            propertyIds: query.searchColumnIds,
        });
    }
    const sorts = query.sort.map(entry => ({
        type: 'property',
        propertyId: entry.propertyId,
        direction: entry.direction,
    }));
    const columnIds = index.columns.map(column => column.id);
    // Select cells hold option ids; the summary check maps a filter label to
    // them, and a sort orders them by the options.
    const columnsWithOptions = index.columns.map(column => ({
        id: column.id,
        name: column.name,
        type: column.type,
        ...(column.options ? { data: { options: column.options } } : {}),
    }));
    const filterColumns = columnsWithOptions;
    const columnTypes = new Map(index.columns.map(column => [column.id, column.type]));
    // Rows are ordered as the table view orders them: each column by its type,
    // a select by the order of its options.
    const sortColumns = (0, database_identity_1.databaseProjectionColumns)(columnsWithOptions);
    const viewOrder = (0, database_identity_1.databaseRowViewOrder)(sorts, sortColumns);
    const labels = new Map(index.columns
        .filter(column => column.options ?? column.grouping?.options)
        .map(column => [
        column.id,
        new Map((column.options ?? column.grouping.options).map(option => [
            option.id,
            option.value,
        ])),
    ]));
    const readValue = (row, id) => row.cells[id]?.value;
    const readSearchValue = (row, id) => {
        const value = row.cells[id]?.value;
        const options = labels.get(id);
        if (!options)
            return value;
        if (Array.isArray(value))
            return value.map(id => options.get(id) ?? id);
        return typeof value === 'string' ? (options.get(value) ?? value) : value;
    };
    const aggregateStates = query.aggregates.map(database_identity_1.createDatabaseProjectionAggregate);
    const grouped = new Map();
    const textGroupColumnIds = new Set(index.columns
        .filter(column => APP_BASE_TEXT_GROUP_TYPES.has(column.type))
        .map(column => column.id));
    function* groupValues(propertyId, value) {
        // A text cell is one key: its exact text. Empty text is an empty cell.
        if (textGroupColumnIds.has(propertyId)) {
            yield value === '' ? null : (value ?? null);
            return;
        }
        const values = Array.isArray(value) ? value : [value ?? null];
        const seen = new Set();
        for (const candidate of values.length ? values : [null]) {
            const identity = appBaseQueryIdentity(candidate);
            if (seen.has(identity))
                continue;
            seen.add(identity);
            yield candidate;
        }
    }
    const addToGroups = (row) => {
        if (!query.groupBy.length)
            return;
        const groupKeys = {};
        const addCombination = (depth) => {
            if (depth < query.groupBy.length) {
                const propertyId = query.groupBy[depth];
                if (propertyId === undefined)
                    return;
                for (const value of groupValues(propertyId, readValue(row, propertyId))) {
                    groupKeys[propertyId] = value;
                    addCombination(depth + 1);
                }
                delete groupKeys[propertyId];
                return;
            }
            const identity = appBaseQueryIdentity(groupKeys);
            let group = grouped.get(identity);
            if (!group) {
                if (grouped.size >= exports.APP_BASE_SOURCE_QUERY_MAX_GROUPS) {
                    throw new AppBaseSourceQueryError('APP_BASE_DATA_BUDGET_EXCEEDED', `Too many distinct values to group by (at most ${exports.APP_BASE_SOURCE_QUERY_MAX_GROUPS}).`, {
                        budget: 'group_count',
                        limit: exports.APP_BASE_SOURCE_QUERY_MAX_GROUPS,
                        requested: grouped.size + 1,
                        sourceKey: index.sourceKey,
                    });
                }
                group = {
                    keys: { ...groupKeys },
                    totalCount: 0,
                    aggregateStates: query.aggregates.map(database_identity_1.createDatabaseProjectionAggregate),
                };
                grouped.set(identity, group);
            }
            group.totalCount += 1;
            for (let aggregate = 0; aggregate < group.aggregateStates.length; aggregate += 1) {
                const request = query.aggregates[aggregate];
                group.aggregateStates[aggregate].add(request.propertyId ? readValue(row, request.propertyId) : row);
            }
        };
        addCombination(0);
    };
    const workIdentity = appBaseQueryIdentity([
        source.workspaceId,
        source.accessScope,
        index.sourceKey,
        index.schemaKey,
        index.columns,
        predicates,
        query.rowIds,
        query.filter,
        sorts,
        query.groupBy,
    ]);
    // Rows of a shard at the same revision are the same rows for the same work
    // and projection, so a reader that holds them needs only their ids.
    const deltaScope = input.deltas
        ? (0, app_base_contract_1.appBaseSha256Hex)(workIdentity + appBaseQueryIdentity(projectedColumnIds))
        : undefined;
    const held = query.since && deltaScope
        ? input.deltas.resolve(query.since, deltaScope)
        : undefined;
    const check = () => {
        if (signal?.aborted) {
            throw new AppBaseSourceQueryError('APP_BASE_QUERY_BUSY', 'App query timed out');
        }
    };
    const scan = {
        readShards: 0,
        skippedShards: 0,
        scannedRows: 0,
    };
    const load = async (ordinal) => {
        check();
        const shard = index.shards[ordinal];
        const cacheKey = 'query-shard:' +
            (0, app_base_contract_1.appBaseSha256Hex)(workIdentity + appBaseQueryIdentity(shard));
        const cached = cache?.get(cacheKey);
        if (cached)
            return cached;
        const chunk = await source.readChunk(shard.shardId);
        check();
        scan.readShards += 1;
        scan.scannedRows += chunk.rows.length;
        if (chunk.shardId !== shard.shardId ||
            chunk.sourceKey !== index.sourceKey ||
            chunk.schemaKey !== index.schemaKey ||
            chunk.revision !== shard.revision) {
            throw new AppBaseSourceQueryError('APP_BASE_QUERY_REVISION_CHANGED', 'App data changed during this query');
        }
        const chunkBytes = byteLength(chunk);
        if (chunkBytes > app_base_contract_1.APP_BASE_BUDGETS.maxReplicaChunkBytes) {
            throw appBaseSourceUnavailableError(index.sourceKey, 'stored_value_too_large');
        }
        const diagnostics = [];
        const filtered = chunk.rows
            .filter(row => !requestedIds || requestedIds.has(row.id))
            .filter(row => !query.filter ||
            (0, database_identity_1.matchesDatabaseViewFilter)(query.filter, id => readValue(row, id), id => columnTypes.get(id)))
            .filter(row => predicates.every(predicate => (0, database_identity_1.matchesDatabaseProjectionPredicate)(predicate, id => readValue(row, id), columnIds, diagnostics, id => readSearchValue(row, id))));
        if (diagnostics.length) {
            throw new AppBaseSourceQueryError('APP_BASE_QUERY_INVALID', diagnostics[0].message);
        }
        const ordered = (0, database_identity_1.sortDatabaseRowsAsView)(filtered, sorts, sortColumns, readValue);
        cache?.set(cacheKey, ordered, byteLength(ordered));
        await yieldControl();
        check();
        return ordered;
    };
    // No row of a shard its value summary rules out can match the predicates
    // or the view filter, so it adds nothing.
    const ruledOut = (shard) => {
        const summary = predicates.length || query.filter
            ? source.shardValueSummary?.(shard.shardId)
            : undefined;
        return (!!summary &&
            (!(0, database_identity_1.databaseShardCanMatch)(summary, predicates) ||
                (!!query.filter &&
                    !(0, database_identity_1.databaseShardCanMatchFilter)(summary, query.filter, filterColumns))));
    };
    // An exact-row read starts every shard a row fingerprint may hold at once,
    // a few at a time: a false positive costs one more parallel read, not one
    // more round trip.
    const prefetched = new Map();
    if (selectedShards) {
        const lanes = [];
        index.shards.forEach((shard, ordinal) => {
            if (!selectedShards.has(shard.shardId) || ruledOut(shard))
                return;
            const lane = prefetched.size % EXACT_ROW_READ_CONCURRENCY;
            const read = (lanes[lane] ?? Promise.resolve()).then(() => load(ordinal));
            // The scan below awaits each read and sees its failure there.
            lanes[lane] = read.catch(() => undefined);
            prefetched.set(ordinal, read);
        });
    }
    const rows = [];
    // The shard ordinal of each window row, for delta windows.
    const rowShards = [];
    // Project only returned rows; predicates, sorting, groups and shard caches
    // still operate on the complete admitted row.
    const selectedColumns = projectedColumnIds
        ? new Set(projectedColumnIds)
        : null;
    const resultColumns = selectedColumns
        ? index.columns.filter(column => selectedColumns.has(column.id))
        : index.columns;
    let windowBytes = 2; // JSON row-array delimiters, before retaining any rows.
    let windowFull = false;
    const append = (row, shard) => {
        const projected = selectedColumns
            ? {
                ...row,
                cells: Object.fromEntries(Object.entries(row.cells).filter(([id]) => selectedColumns.has(id))),
            }
            : row;
        const bytes = byteLength(projected) + (rows.length ? 1 : 0);
        // A first allowed row must make progress even when it exceeds this
        // candidate. Stored-cell/shard policy remains the existing authority.
        if (rows.length && windowBytes + bytes > maxWindowBytes) {
            windowFull = true;
            return;
        }
        windowBytes += bytes;
        rows.push(projected);
        rowShards.push(shard);
    };
    const needsScan = requestedIds !== null ||
        predicates.length > 0 ||
        Boolean(query.filter) ||
        sorts.length > 0 ||
        query.aggregates.length > 0 ||
        query.groupBy.length > 0 ||
        !source.exactRowCounts;
    let totalCount = 0;
    const heads = [];
    let workingBytes = 0;
    const headFor = (shard, offset, row) => {
        const serialized = JSON.stringify(viewOrder.keys(id => readValue(row, id)));
        const headBytes = 128 + serialized.length * 8;
        if (workingBytes + headBytes > maxWorkingBytes) {
            throw appBaseSourceUnavailableError(index.sourceKey, 'sort_values_too_large');
        }
        workingBytes += headBytes;
        return {
            shard,
            offset,
            keys: JSON.parse(serialized),
            workingBytes: headBytes,
        };
    };
    const compare = (left, right) => viewOrder.compare(left.keys, right.keys) ||
        left.shard - right.shard ||
        left.offset - right.offset;
    // The rows of each shard the merge takes its heads from, as the scan read
    // them: a sorted read loads every shard once, not once per row it delivers.
    // No shard gives the window more than offset + limit rows, so only those stay.
    const merged = new Map();
    const insert = (head) => {
        let lo = 0;
        let hi = heads.length;
        while (lo < hi) {
            const mid = (lo + hi) >>> 1;
            if (compare(heads[mid], head) <= 0)
                lo = mid + 1;
            else
                hi = mid;
        }
        heads.splice(lo, 0, head);
    };
    for (let ordinal = 0; ordinal < index.shards.length; ordinal += 1) {
        check();
        const shard = index.shards[ordinal];
        if (selectedShards && !selectedShards.has(shard.shardId))
            continue;
        if (!needsScan &&
            (totalCount + shard.rowCount <= query.offset ||
                rows.length >= query.limit ||
                windowFull)) {
            totalCount += shard.rowCount;
            if (ordinal % 256 === 0)
                await yieldControl();
            continue;
        }
        if (ruledOut(shard)) {
            scan.skippedShards += 1;
            if (ordinal % 256 === 0)
                await yieldControl();
            continue;
        }
        const matched = await (prefetched.get(ordinal) ?? load(ordinal));
        if (source.exactRowCounts &&
            !needsScan &&
            matched.length !== shard.rowCount) {
            throw new AppBaseSourceQueryError('APP_BASE_QUERY_REVISION_CHANGED', 'App row manifest changed');
        }
        if (sorts.length && matched.length) {
            merged.set(ordinal, matched.slice(0, query.offset + query.limit));
            insert(headFor(ordinal, 0, matched[0]));
        }
        for (let rowOffset = 0; rowOffset < matched.length; rowOffset += 1) {
            const row = matched[rowOffset];
            addToGroups(row);
            for (let aggregate = 0; aggregate < aggregateStates.length; aggregate += 1) {
                const request = query.aggregates[aggregate];
                aggregateStates[aggregate].add(request.propertyId ? readValue(row, request.propertyId) : row);
            }
            if (!sorts.length &&
                totalCount + rowOffset >= query.offset &&
                rows.length < query.limit &&
                !windowFull) {
                append(row, ordinal);
            }
        }
        totalCount += matched.length;
    }
    if (sorts.length) {
        let seen = 0;
        while (heads.length && rows.length < query.limit && !windowFull) {
            check();
            const head = heads.shift();
            workingBytes -= head.workingBytes;
            const matched = merged.get(head.shard);
            if (seen++ >= query.offset)
                append(matched[head.offset], head.shard);
            if (rows.length >= query.limit || windowFull)
                break;
            const next = head.offset + 1;
            if (next < matched.length) {
                insert(headFor(head.shard, next, matched[next]));
            }
            if (seen % 256 === 0)
                await yieldControl();
        }
    }
    check();
    // The window keeps the bounds of a full read; only rows the reader holds
    // unchanged leave its payload: by row revision where the source names
    // them, else every row of a shard it holds at its current revision.
    const rowRevisions = deltaScope && source.rowRevision
        ? new Map(rows.map(row => [row.id, source.rowRevision(row)]))
        : undefined;
    const unchanged = (row, shard) => rowRevisions
        ? held?.rows?.get(row.id) === rowRevisions.get(row.id)
        : held?.shards.get(index.shards[shard].shardId) ===
            index.shards[shard].revision;
    // Row revisions of a token keep the order of its window: a window with
    // the same ids in the same order need not name them again.
    const sameOrder = (() => {
        if (!query.sameOrder ||
            !held?.rows ||
            !rowRevisions ||
            held.rows.size !== rows.length)
            return false;
        let position = 0;
        for (const id of held.rows.keys())
            if (rows[position++].id !== id)
                return false;
        return true;
    })();
    const delta = !held
        ? {}
        : sameOrder
            ? { sameOrder: true }
            : { order: rows.map(row => row.id) };
    const result = {
        sourceKey: index.sourceKey,
        columns: resultColumns,
        rows: held
            ? rows.filter((row, position) => !unchanged(row, rowShards[position]))
            : rows,
        ...delta,
        ...(deltaScope
            ? {
                deltaToken: input.deltas.remember(deltaScope, revisionVector, rowRevisions),
            }
            : {}),
        totalCount,
        offset: query.offset,
        limit: windowFull ? rows.length : query.limit,
        complete: true,
        source: source.resultSource ?? 'server-chunks',
        diagnostics: [],
        aggregates: aggregateStates.map(state => state.result(true)),
        groups: [...grouped.values()].map(group => ({
            keys: group.keys,
            totalCount: group.totalCount,
            aggregates: group.aggregateStates.map(state => state.result(true)),
        })),
        revision,
        // The state of every shard read as one entry: windows of one state
        // carry the same entry, and readers compare it whole (complete-dataset,
        // source-windows, the delivery of the server). A reader of an older
        // build takes it as the one shard of its source.
        revisionVector: [
            {
                shardId: exports.APP_BASE_SOURCE_QUERY_STATE_SHARD,
                revision: (0, app_base_contract_1.appBaseSha256Hex)(appBaseQueryIdentity(revisionVector)),
            },
        ],
        schemaKey: index.schemaKey,
    };
    input.onScan?.(scan);
    return result;
}
/**
 * A query with further windows (`windows`): the first window as
 * executePortableAppBaseSourceQuery answers it, and each further window as
 * its own read on the same input and state. A further window is never larger
 * than the first (whose limit the host has already bound) and lies within
 * the rows one query reads; a complete read without `since` asks at most
 * APP_BASE_COMPLETE_READ_WINDOWS. The windows come while the answer stays
 * within the window bytes, those of a complete read while they hold rows;
 * the reader reads the others itself.
 * Without `windows` it is executePortableAppBaseSourceQuery.
 */
async function executePortableAppBaseSourceQueryWindows(input) {
    const { windows, ...query } = input.query;
    const complete = query.since === undefined;
    if (windows &&
        ((complete && windows.length > app_base_bridge_budgets_1.APP_BASE_COMPLETE_READ_WINDOWS) ||
            windows.some(window => window.limit > query.limit ||
                window.offset - query.offset >= app_base_contract_1.APP_BASE_BUDGETS.maxDatasetRows)))
        throw new AppBaseSourceQueryError('APP_BASE_QUERY_INVALID', 'Further windows exceed the window of the first or the rows of one read');
    const first = await executePortableAppBaseSourceQuery({ ...input, query });
    // A first window of a complete read cut at its bytes moves every further one.
    if (!windows || (complete && first.limit < query.limit))
        return first;
    const further = [];
    const maxBytes = input.maxWindowBytes ?? APP_BASE_QUERY_WINDOW_BYTES;
    let bytes = byteLength(first.rows);
    for (const window of windows) {
        if (complete && window.offset >= first.totalCount)
            break;
        const { rows, offset, limit, order, sameOrder, deltaToken } = await executePortableAppBaseSourceQuery({
            ...input,
            query: { ...query, ...window },
        });
        bytes += byteLength(rows);
        if (bytes > maxBytes)
            break;
        further.push({ rows, offset, limit, order, sameOrder, deltaToken });
        // A window cut at its bytes moves the ones after it.
        if (complete && limit < window.limit)
            break;
    }
    return further.length ? { ...first, windows: further } : first;
}
exports.APP_BASE_SOURCE_QUERY_TRANSPORT_PROTOCOL = 'streamlain-source-query-transport-v1';
const transportToken = (offset, digest) => `v1.${offset}.${digest}`;
const transportBody = (result) => {
    const serialized = JSON.stringify(result);
    return {
        revision: result.revision,
        serialized,
        digest: (0, app_base_contract_1.appBaseSha256Hex)(serialized),
    };
};
const TRANSPORT_CONTINUATION = /^v1\.(\d+)\.([a-f0-9]{64})$/;
/** The offset and digest a continuation names; a forged one is invalid. */
function transportContinuation(continuation) {
    const match = TRANSPORT_CONTINUATION.exec(continuation);
    if (!match || !Number.isSafeInteger(Number(match[1]))) {
        throw new AppBaseSourceQueryError('APP_BASE_QUERY_INVALID', 'Invalid App transport continuation');
    }
    return { offset: Number(match[1]), digest: match[2] };
}
function transportPageOf(body, continuation, maxBytes) {
    if (!Number.isSafeInteger(maxBytes) || maxBytes < 1024) {
        throw new AppBaseSourceQueryError('APP_BASE_QUERY_INVALID', 'Invalid App transport page budget');
    }
    const { serialized, digest } = body;
    let offset = 0;
    if (continuation !== undefined) {
        const named = transportContinuation(continuation);
        offset = named.offset;
        if (named.digest !== digest || offset <= 0 || offset >= serialized.length) {
            throw new AppBaseSourceQueryError('APP_BASE_QUERY_REVISION_CHANGED', 'App transport snapshot is no longer available');
        }
    }
    const page = (end) => ({
        protocol: exports.APP_BASE_SOURCE_QUERY_TRANSPORT_PROTOCOL,
        revision: body.revision,
        fragment: serialized.slice(offset, end),
        continuation: end < serialized.length ? transportToken(end, digest) : null,
    });
    // Reserve the longest continuation envelope once. Walk the existing JSON
    // string, counting its escaped UTF-8 bytes without serializing candidate
    // fragments. A held read visits each result character only once.
    const envelopeBytes = byteLength({
        protocol: exports.APP_BASE_SOURCE_QUERY_TRANSPORT_PROTOCOL,
        revision: body.revision,
        fragment: '',
        continuation: transportToken(serialized.length, digest),
    });
    let remaining = maxBytes - envelopeBytes;
    let selected = offset;
    while (selected < serialized.length) {
        const code = serialized.charCodeAt(selected);
        let units = 1;
        let bytes;
        if (code === 0x22 || code === 0x5c)
            bytes = 2;
        else if (code < 0x20)
            bytes = 6;
        else if (code < 0x80)
            bytes = 1;
        else if (code < 0x800)
            bytes = 2;
        else if (code >= 0xd800 && code <= 0xdbff) {
            const next = serialized.charCodeAt(selected + 1);
            if (next >= 0xdc00 && next <= 0xdfff) {
                units = 2;
                bytes = 4;
            }
            else
                bytes = 6;
        }
        else if (code >= 0xdc00 && code <= 0xdfff)
            bytes = 6;
        else
            bytes = 3;
        if (bytes > remaining)
            break;
        remaining -= bytes;
        selected += units;
    }
    if (selected === offset) {
        throw new AppBaseSourceQueryError('APP_BASE_SOURCE_UNAVAILABLE', 'App transport cannot create a bounded page');
    }
    return page(selected);
}
/**
 * Splits the serialized public result behind the immutable Bridge. App code
 * never sees these pages or their byte budget; concatenating the fragments is
 * byte-for-byte identical to the unsplit query result, including oversized
 * individual cell values.
 */
function createAppBaseSourceQueryTransportPage(result, continuation, maxBytes = app_base_contract_1.APP_BASE_BUDGETS.maxDatasetChunkBytes) {
    return transportPageOf(transportBody(result), continuation, maxBytes);
}
/** How long a split read keeps the state it began on, and how much a server holds of them. */
exports.APP_BASE_SOURCE_QUERY_TRANSPORT_HOLD_MS = 2 * 60_000;
exports.APP_BASE_SOURCE_QUERY_TRANSPORT_HOLD_BYTES = 64 * 1024 * 1024;
/**
 * Values a server keeps for the reads that still continue on them: each one
 * for `holdMs` after it was kept or last read, the oldest leave first beyond
 * `holdBytes` (`bytesOf` names what one holds in memory); one larger than
 * `holdBytes` is not kept. The split pages of one read and the windows of one
 * complete read both end on the state they began on with it.
 */
function createAppBaseHeldValues({ holdMs = exports.APP_BASE_SOURCE_QUERY_TRANSPORT_HOLD_MS, holdBytes = exports.APP_BASE_SOURCE_QUERY_TRANSPORT_HOLD_BYTES, bytesOf, onDrop, now = Date.now, }) {
    const held = new Map();
    let bytes = 0;
    const drop = (key, dispose = true) => {
        const entry = held.get(key);
        if (!entry)
            return;
        held.delete(key);
        bytes -= entry.bytes;
        if (dispose)
            onDrop?.(entry.value);
    };
    const expire = () => {
        const at = now();
        for (const [key, entry] of held)
            if (entry.until <= at)
                drop(key);
    };
    return {
        /** The value kept under `key`; a read keeps it `holdMs` longer. */
        get(key) {
            expire();
            const entry = held.get(key);
            if (!entry)
                return undefined;
            // The newest read leaves last.
            held.delete(key);
            held.set(key, { ...entry, until: now() + holdMs });
            return entry.value;
        },
        keep(key, value) {
            expire();
            const size = bytesOf(value);
            drop(key, held.get(key)?.value !== value);
            if (size > holdBytes) {
                onDrop?.(value);
                return;
            }
            while (held.size && bytes + size > holdBytes)
                drop(held.keys().next().value);
            held.set(key, { value, bytes: size, until: now() + holdMs });
            bytes += size;
        },
        drop: (key) => drop(key),
        clear() {
            for (const key of held.keys())
                drop(key);
        },
        /** What is kept now: values and their bytes in memory. */
        get size() {
            expire();
            return { values: held.size, bytes };
        },
    };
}
/**
 * How long a host knows the shard revisions of a result it delivered, and
 * how much memory that takes at most (initial settings, not measurements):
 * a page that reads again within this time reads only what changed.
 */
exports.APP_BASE_SOURCE_QUERY_DELTA_HOLD_MS = 30 * 60_000;
exports.APP_BASE_SOURCE_QUERY_DELTA_HOLD_BYTES = 8 * 1024 * 1024;
/** Tokens are small and name a held revision vector; viewers of one table
 * with different access share its vector. */
exports.APP_BASE_SOURCE_QUERY_DELTA_TOKEN_HOLD_BYTES = 1024 * 1024;
/**
 * A reader that holds a complete result sends its delta token as `since`;
 * the next window then leaves out the rows it holds unchanged: by row
 * revision where the source names them (rowRevision of the reader), else
 * the rows of shards whose revision is unchanged. The token is bound to the work, projection and access scope of
 * the result, so it never omits rows another scope would see differently. A
 * host that no longer knows a token answers the window in full.
 */
function createAppBaseSourceQueryDeltas({ holdMs = exports.APP_BASE_SOURCE_QUERY_DELTA_HOLD_MS, holdBytes = exports.APP_BASE_SOURCE_QUERY_DELTA_HOLD_BYTES, now = Date.now, } = {}) {
    // Revision vectors by their identity; a token names the scope it was
    // issued for and one vector. A token whose vector left reads in full.
    const vectors = createAppBaseHeldValues({ holdMs, holdBytes, bytesOf: state => state.bytes, now });
    const tokens = createAppBaseHeldValues({
        holdMs,
        holdBytes: exports.APP_BASE_SOURCE_QUERY_DELTA_TOKEN_HOLD_BYTES,
        bytesOf: state => state.bytes,
        now,
    });
    // A result with row revisions needs no vector: its token holds a revision
    // per row, in a budget of its own as large as the one of the vectors.
    const rowTokens = createAppBaseHeldValues({
        holdMs,
        holdBytes,
        bytesOf: state => state.bytes,
        now,
    });
    const deltas = {
        resolve(token, scope) {
            const byRow = rowTokens.get(token);
            if (byRow)
                return byRow.scope === scope
                    ? { shards: new Map(), rows: byRow.rows }
                    : undefined;
            const state = tokens.get(token);
            const shards = state?.scope === scope ? vectors.get(state.vector)?.shards : undefined;
            return shards && { shards };
        },
        remember(scope, revisionVector, rowRevisions) {
            // A token of row revisions names its rows: the same rows of a scope
            // have the same token, in every process.
            if (rowRevisions) {
                const token = (0, app_base_contract_1.appBaseSha256Hex)(scope + appBaseQueryIdentity([...rowRevisions]));
                if (!rowTokens.get(token)) {
                    let bytes = scope.length * 2 + 192;
                    for (const [id, revision] of rowRevisions)
                        bytes += (id.length + revision.length) * 2 + 64;
                    rowTokens.keep(token, { scope, rows: rowRevisions, bytes });
                }
                return token;
            }
            const vector = (0, app_base_contract_1.appBaseSha256Hex)(appBaseQueryIdentity(revisionVector));
            if (!vectors.get(vector)) {
                // Ids and revisions are strings of the index; the map holds two
                // references and its entry per shard.
                let bytes = vector.length * 2;
                for (const shard of revisionVector)
                    bytes += (shard.shardId.length + shard.revision.length) * 2 + 64;
                vectors.keep(vector, {
                    shards: new Map(revisionVector.map(shard => [shard.shardId, shard.revision])),
                    bytes,
                });
            }
            const token = (0, app_base_contract_1.appBaseSha256Hex)(scope + vector);
            if (!tokens.get(token))
                tokens.keep(token, {
                    scope,
                    vector,
                    bytes: (scope.length + vector.length + token.length) * 2,
                });
            return token;
        },
    };
    return deltas;
}
/**
 * The split reads of one server: a read in several pages finishes on the
 * state it began on, also when rows change between its pages (every agent
 * writes while a page reads; one write must not break a read). A first page
 * that has a continuation keeps its serialized result (createAppBaseHeldValues);
 * its continuations come from it without reading the rows again, for the same
 * `scope` only (the source and the access of the viewer who began it: a
 * continuation never reaches rows another viewer read). A continuation of a
 * result no longer held falls back to the current result and answers
 * REVISION_CHANGED when it differs, as before.
 */
function createAppBaseSourceQueryTransportHold({ holdMs = exports.APP_BASE_SOURCE_QUERY_TRANSPORT_HOLD_MS, holdBytes = exports.APP_BASE_SOURCE_QUERY_TRANSPORT_HOLD_BYTES, maxBytes = app_base_contract_1.APP_BASE_BUDGETS.maxDatasetChunkBytes, now = Date.now, } = {}) {
    const held = createAppBaseHeldValues({
        holdMs,
        holdBytes,
        // A JavaScript string holds two bytes per UTF-16 unit in memory.
        bytesOf: body => body.serialized.length * 2,
        now,
    });
    const keyOf = (scope, digest) => JSON.stringify([scope, digest]);
    const preparedPage = async (scope, continuation, read) => {
        if (continuation !== undefined) {
            const key = keyOf(scope, transportContinuation(continuation).digest);
            const body = held.get(key);
            if (body) {
                const page = transportPageOf(body, continuation, maxBytes);
                if (page.continuation === null)
                    held.drop(key);
                return { page };
            }
        }
        const { result, body } = await read();
        const page = transportPageOf(body, continuation, maxBytes);
        if (continuation === undefined && page.continuation !== null)
            held.keep(keyOf(scope, body.digest), body);
        return { page, result };
    };
    return {
        /** Reuse a representation already serialized by the admitted query cache. */
        preparedPage,
        /** Admit the caller before entering this hold, including continuations. */
        page: (scope, continuation, read) => preparedPage(scope, continuation, async () => {
            const result = await read();
            return { result, body: transportBody(result) };
        }),
        /** What the hold keeps now: reads and their bytes in memory. */
        get size() {
            const { values, bytes } = held.size;
            return { reads: values, bytes };
        },
    };
}
